import React, { createContext, useContext, useState, useEffect } from 'react';
import { getClassificationMetadata } from '../services/api';

/**
 * ClassificationLabelsContext
 *
 * Provides classification metadata (levels, compartments with categories,
 * dissemination controls, country codes, special groups) to the component tree.
 * Data is fetched once on mount from the /classification/metadata API endpoint.
 *
 * Falls back to a hardcoded default if the API call fails, so the UI is always
 * functional even when the backend is unavailable.
 */

const FALLBACK_METADATA = {
  classification_levels: ['UNCLASSIFIED', 'CONFIDENTIAL', 'SECRET', 'TOP SECRET'],
  compartments: [
    'SI', 'GAMMA', 'UMBRA', 'MORAY', 'SAR',
    'TK', 'RUFF', 'ZARF', 'BYEMAN',
    'HCS', 'SPOKE', 'MEDIAN',
    'ECI', 'SAP', 'WAIVED SAP',
    'RD', 'FRD', 'CNWDI', 'SIGMA', 'DELTA',
    'FISA'
  ],
  compartment_categories: {
    SIGINT: {
      label: 'Signals Intelligence (SIGINT)',
      description: 'Intelligence derived from electronic signals and systems',
      compartments: ['SI', 'GAMMA', 'UMBRA', 'MORAY', 'SAR'],
    },
    IMINT: {
      label: 'Imagery Intelligence (IMINT)',
      description: 'Intelligence derived from imagery collected by satellites, aircraft, or other platforms',
      compartments: ['TK', 'RUFF', 'ZARF', 'BYEMAN'],
    },
    HUMINT: {
      label: 'Human Intelligence (HUMINT)',
      description: 'Intelligence derived from human sources',
      compartments: ['HCS', 'SPOKE', 'MEDIAN'],
    },
    Nuclear: {
      label: 'Nuclear & Weapons',
      description: 'Nuclear weapons and restricted data programs',
      compartments: ['RD', 'FRD', 'CNWDI', 'SIGMA', 'DELTA'],
    },
    'Special Programs': {
      label: 'Special Access Programs',
      description: 'Exceptionally controlled access programs',
      compartments: ['ECI', 'SAP', 'WAIVED SAP'],
    },
    Legal: {
      label: 'Legal Authorities',
      description: 'Legal-authority-based compartments',
      compartments: ['FISA'],
    },
  },
  compartment_descriptions: {
    SI: 'Special Intelligence - SIGINT-derived information',
    GAMMA: 'GAMMA - Highly sensitive SIGINT subcategory',
    UMBRA: 'UMBRA - Sensitive SIGINT collection methods',
    MORAY: 'MORAY - SIGINT compartment for specific collection',
    SAR: 'Special Access Required - Highly restricted SIGINT',
    TK: 'TALENT KEYHOLE - Satellite imagery intelligence',
    RUFF: 'RUFF - Imagery intelligence compartment',
    ZARF: 'ZARF - Imagery intelligence compartment',
    BYEMAN: 'BYEMAN - NRO satellite reconnaissance programs',
    HCS: 'HUMINT Control System - Protects human sources',
    SPOKE: 'SPOKE - HUMINT compartment for source protection',
    MEDIAN: 'MEDIAN - HUMINT compartment for sensitive sources',
    RD: 'Restricted Data - Nuclear weapons design information (Atomic Energy Act)',
    FRD: 'Formerly Restricted Data - Nuclear weapons effects/military use',
    CNWDI: 'Critical Nuclear Weapon Design Information',
    SIGMA: 'SIGMA - Nuclear weapon design categories',
    DELTA: 'DELTA - Nuclear weapon design vulnerability data',
    ECI: 'Exceptionally Controlled Information',
    SAP: 'Special Access Program - Need-to-know beyond normal clearance',
    'WAIVED SAP': 'Waived SAP - Congressional notification waived',
    FISA: 'Foreign Intelligence Surveillance Act - Court-authorized intelligence',
  },
  dissemination_controls: [
    'NOFORN', 'ORCON', 'FOUO', 'LIMDIS', 'NODIS',
    'EXDIS', 'STADIS', 'EYES ONLY', 'DISPLAY ONLY', 'ORIGINATOR CONTROLLED'
  ],
  dissemination_descriptions: {
    NOFORN: 'Not Releasable to Foreign Nationals - US eyes only',
    ORCON: 'Originator Controlled - Requires originator approval for dissemination',
    FOUO: 'For Official Use Only - Limited to official government use',
    LIMDIS: 'Limited Distribution - Restricted to specified recipients',
    NODIS: 'No Distribution - Extremely restricted dissemination',
    EXDIS: 'Exclusive Distribution - Limited to named recipients only',
    STADIS: 'Special Technical and Administrative Distribution',
    'EYES ONLY': 'Eyes Only - No electronic transmission permitted',
    'DISPLAY ONLY': 'Display Only - No reproduction permitted',
    'ORIGINATOR CONTROLLED': 'Full originator control over all dissemination',
  },
  country_codes: {
    ALB: 'Albania', BEL: 'Belgium', BGR: 'Bulgaria', CAN: 'Canada',
    HRV: 'Croatia', CZE: 'Czech Republic', DNK: 'Denmark', EST: 'Estonia',
    FIN: 'Finland', FRA: 'France', DEU: 'Germany', GRC: 'Greece',
    HUN: 'Hungary', ISL: 'Iceland', ITA: 'Italy', LVA: 'Latvia',
    LTU: 'Lithuania', LUX: 'Luxembourg', MNE: 'Montenegro', NLD: 'Netherlands',
    MKD: 'North Macedonia', NOR: 'Norway', POL: 'Poland', PRT: 'Portugal',
    ROU: 'Romania', SVK: 'Slovakia', SVN: 'Slovenia', ESP: 'Spain',
    SWE: 'Sweden', TUR: 'Turkey', GBR: 'United Kingdom', USA: 'United States',
    JPN: 'Japan', KOR: 'South Korea', ISR: 'Israel', EGY: 'Egypt',
    JOR: 'Jordan', KWT: 'Kuwait', MAR: 'Morocco', PAK: 'Pakistan',
    PHL: 'Philippines', THA: 'Thailand', TWN: 'Taiwan', ARG: 'Argentina',
    BRA: 'Brazil', AFG: 'Afghanistan', IRQ: 'Iraq', IND: 'India',
    MEX: 'Mexico', COL: 'Colombia', CHL: 'Chile', URY: 'Uruguay',
    SGP: 'Singapore', MYS: 'Malaysia', IDN: 'Indonesia', VNM: 'Vietnam',
  },
  special_groups: {
    FVEY: 'Five Eyes (USA, GBR, CAN, AUS, NZL)',
    NATO: 'North Atlantic Treaty Organization',
    QUAD: 'Quadrilateral Security Dialogue (USA, JPN, IND, AUS)',
    AUKUS: 'Australia-United Kingdom-United States',
    ANZUS: 'Australia-New Zealand-United States',
  },
  fvey_countries: {
    USA: 'United States', GBR: 'United Kingdom', CAN: 'Canada',
    AUS: 'Australia', NZL: 'New Zealand',
  },
  nato_countries: {
    ALB: 'Albania', BEL: 'Belgium', BGR: 'Bulgaria', CAN: 'Canada',
    HRV: 'Croatia', CZE: 'Czech Republic', DNK: 'Denmark', EST: 'Estonia',
    FIN: 'Finland', FRA: 'France', DEU: 'Germany', GRC: 'Greece',
    HUN: 'Hungary', ISL: 'Iceland', ITA: 'Italy', LVA: 'Latvia',
    LTU: 'Lithuania', LUX: 'Luxembourg', MNE: 'Montenegro', NLD: 'Netherlands',
    MKD: 'North Macedonia', NOR: 'Norway', POL: 'Poland', PRT: 'Portugal',
    ROU: 'Romania', SVK: 'Slovakia', SVN: 'Slovenia', ESP: 'Spain',
    SWE: 'Sweden', TUR: 'Turkey', GBR: 'United Kingdom', USA: 'United States',
  },
};

const ClassificationLabelsContext = createContext(null);

export function ClassificationLabelsProvider({ children }) {
  const [metadata, setMetadata] = useState(FALLBACK_METADATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMetadata = async () => {
      try {
        const data = await getClassificationMetadata();
        if (!cancelled) {
          setMetadata(data);
          setError(null);
        }
      } catch (err) {
        console.warn('Failed to fetch classification metadata, using fallback:', err.message);
        if (!cancelled) {
          setError(err.message);
          // Keep fallback metadata
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ClassificationLabelsContext.Provider value={{ ...metadata, loading, error }}>
      {children}
    </ClassificationLabelsContext.Provider>
  );
}

export function useClassificationLabels() {
  const context = useContext(ClassificationLabelsContext);
  if (!context) {
    throw new Error('useClassificationLabels must be used within a ClassificationLabelsProvider');
  }
  return context;
}

export default ClassificationLabelsContext;
